import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AliasService } from '../../services/alias.service';
import { SearchBarComponent } from '../../shared/searchbar/searchbar.component';
import { SharedTableComponent } from '../../shared/shared-table/shared-table.component';

type DiagnosticsResponse = {
  documentList: any[];
  totalDocuments: number;
  currentPage?: number;
  totalPages?: number;
  limit?: number;
  offset?: number;
};

@Component({
  selector: 'app-diagnostics',
  standalone: true,
  templateUrl: './diagnostics.component.html',
  styleUrls: ['./diagnostics.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    SharedTableComponent,
    SearchBarComponent
  ]
})
export class DiagnosticsComponent implements OnInit {
  constructor(
    private readonly aliasService: AliasService,
    private readonly router: Router
  ) {}

  selectedAlias: number | null = null;
  aliasOptions: Array<{ value: number; label: string }> = [];

  // dati tabella
  dataSourceAliasDocuments = new MatTableDataSource<any>();

  isLoading = false;
  hasError = false;

  // query/paginazione
  q: string | null = null;
  limit = 10; //pageSize iniziale
  offset = 0;
  pageIndex = 0;
  totalItems = 0;

  //ordinamento
  sort = 'title';
  direction = 'asc';
  sortBy: string = '';

  ngOnInit(): void {
    this.loadAliasListAll();
  }

  private loadAliasListAll(): void {
    this.aliasService.getAliasListAll().subscribe((response: any) => {
      const options = response as Array<{ id: number; name: string }>;
      this.aliasOptions = options.map((x) => ({
        value: x.id,
        label: x.name
      }));
    });
  }

  private loadListDocuments(limit: number, offset: number, aliasId: number, sortBy: string, q?: string | null): void {
    this.isLoading = true;
    this.hasError = false;
    this.aliasService.getAliasDocuments(limit, offset, aliasId, sortBy, q ?? null).pipe(finalize(() => { this.isLoading = false; })).subscribe({
      next: (response: DiagnosticsResponse) => {
        this.dataSourceAliasDocuments.data = response?.documentList ?? [];
        this.totalItems = response?.totalDocuments ?? this.dataSourceAliasDocuments.data.length;
        
        // allinea lo stato locale con quanto ritorna il BE
        this.pageIndex = (response?.currentPage ?? 1) - 1; // currentPage - 1 torna pageIndex (0-based)
        this.limit  = response?.limit ?? limit; // limit è pageSize
        this.offset = response?.offset ?? (this.pageIndex * this.limit);
      },
      error: (err) => {
        console.error('Si è verificato un errore nel caricamento dei documenti', err);
        this.dataSourceAliasDocuments.data = [];
        this.totalItems = 0;
        this.hasError = true;
      }
    });
  }

  mostraDocumenti(): void {
    if (!this.selectedAlias) {
      console.warn('Selezionare un alias prima di procedere.');
      return;
    }
    this.pageIndex = 0;
    this.offset = 0;
    this.loadListDocuments(this.limit, this.offset, this.selectedAlias, this.sortBy, this.q);
  }

  onAliasChanged(): void {
    this.pageIndex = 0;
    this.offset = 0;
  }

  onFilterChanged(value: any): void {
    this.q = (value?.q ?? '').toString();
    this.pageIndex = 0;
    this.offset = 0;
    if (this.selectedAlias) {
      this.loadListDocuments(this.limit, this.offset, this.selectedAlias, this.sortBy, this.q);
    }
  }

  // Ordinamento
  onSortChanged(sort: Sort) {
    this.sort = sort.active;
    this.direction = sort.direction || 'asc';
    this.sortBy = `${this.sort}:${this.direction}`;
    console.log('sorting', this.sortBy);
    this.pageIndex = 0;
    if (this.selectedAlias) {
      this.loadListDocuments(this.limit, this.offset, this.selectedAlias, this.sortBy, this.q);
    }
  }

  onPageChanged(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.limit = event.pageSize;
    this.offset = this.pageIndex * this.limit;

    if (this.selectedAlias) {
      this.loadListDocuments(this.limit, this.offset, this.selectedAlias, this.sortBy, this.q);
    }
  }

  goToDetail(row: any): void {
    try {
      localStorage.setItem('selectedAliasDocumentRow', JSON.stringify(row));
    } catch {}
    this.router.navigate(['/diagnostica/dettaglio', row.id, encodeURIComponent(row.title)]);
  }
}